import React, { useState } from 'react';
import {
  Box, Typography, Stack, TextField, Button, IconButton, Card, Avatar,
  BottomNavigation, BottomNavigationAction, Chip, CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Timeline as ProgressIcon,
  GroupAdd as JoinIcon,
  Person as PersonIcon,
  QrCodeScanner as QrIcon,
  CheckCircle as CheckIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import { CLASS, TEACHER, APP_LOGOS } from '../sharedMockData';

/**
 * Class-code entry screen. Matches iCivics Student Join Class flow.
 * Cycles through entry → submitting → success states via a button.
 */

const STATES = ['entry', 'submitting', 'success'];

const AndroidJoinClassMockup = () => {
  const [state, setState] = useState('entry');
  const [code, setCode] = useState(CLASS.code);

  const boxes = code.padEnd(6, ' ').split('').slice(0, 6);

  const advance = () => {
    const idx = STATES.indexOf(state);
    setState(STATES[(idx + 1) % STATES.length]);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
      <PhoneFrame background="#f6f7fb">
        <AutomatrixBanner />

        {/* App bar */}
        <Box sx={{
          px: 2, pt: 1, pb: 1.25, bgcolor: '#fff',
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid #ececf2',
        }}>
          <IconButton size="small"><BackIcon sx={{ fontSize: 20 }} /></IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
            Join a Class
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            onClick={advance}
            sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#8a8d93' }}
          >
            Next state
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pt: 2 }}>
          {/* Hero */}
          <Stack alignItems="center" spacing={1.25} sx={{ py: 2 }}>
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'linear-gradient(135deg, #7e57c2 0%, #5e35b1 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(126,87,194,0.3)',
            }}>
              <AssignmentIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
              Enter class code
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b6f76', textAlign: 'center', maxWidth: 260, fontSize: '0.72rem' }}>
              Ask your teacher for the 6-character code, or scan the QR code they shared.
            </Typography>
          </Stack>

          {state === 'entry' && (
            <>
              {/* 6 character pill */}
              <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mb: 2 }}>
                {boxes.map((ch, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 42, height: 54, borderRadius: 3,
                      border: ch.trim() ? '2px solid #7e57c2' : '2px solid #dadce0',
                      bgcolor: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 700,
                      color: ch.trim() ? '#5e35b1' : '#c3c6cf',
                      boxShadow: ch.trim() ? '0 2px 6px rgba(126,87,194,0.15)' : 'none',
                    }}
                  >
                    {ch.trim() || '—'}
                  </Box>
                ))}
              </Stack>

              <TextField
                size="small"
                fullWidth
                label="Class code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                sx={{ mb: 1.5 }}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.18em', borderRadius: 2 },
                }}
              />

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setState('submitting')}
                sx={{
                  borderRadius: 28, textTransform: 'none', fontWeight: 700,
                  py: 1.1, bgcolor: '#5e35b1',
                  '&:hover': { bgcolor: '#4527a0' },
                }}
              >
                Join class
              </Button>

              <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: '#e6e8ef' }} />
                <Typography variant="caption" sx={{ color: '#8a8d93', fontSize: '0.65rem' }}>
                  OR
                </Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: '#e6e8ef' }} />
              </Stack>

              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<QrIcon />}
                sx={{
                  borderRadius: 28, textTransform: 'none', fontWeight: 600,
                  color: '#5e35b1', borderColor: '#d1c4e9',
                }}
              >
                Scan QR code
              </Button>

              <Card
                elevation={0}
                sx={{
                  mt: 3, p: 1.5, borderRadius: 3,
                  bgcolor: '#fff', border: '1px solid #e6e8ef',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1f1f1f', fontSize: '0.72rem' }}>
                  How it works
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#6b6f76', mt: 0.5, fontSize: '0.68rem', lineHeight: 1.45 }}>
                  Your code is relayed to the partner app (iCivics, Khan, etc.) via AchieveDXP.
                  Your teacher will see you as joined within a few seconds.
                </Typography>
              </Card>
            </>
          )}

          {state === 'submitting' && (
            <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
              <CircularProgress sx={{ color: '#5e35b1' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                Joining class…
              </Typography>
              <Stack spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  label="Relaying to iCivics via AchieveDXP"
                  sx={{ bgcolor: '#ede7f6', color: '#5e35b1', fontSize: '0.65rem', fontWeight: 600 }}
                />
                <Typography variant="caption" sx={{ color: '#8a8d93', fontSize: '0.65rem' }}>
                  code: {code}
                </Typography>
              </Stack>
            </Stack>
          )}

          {state === 'success' && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: '#e8f5e9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckIcon sx={{ fontSize: 42, color: '#2e7d32' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                You're in!
              </Typography>

              <Card elevation={0} sx={{
                width: '100%', p: 2, borderRadius: 3,
                bgcolor: '#fff', border: '1px solid #e6e8ef',
              }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Avatar
                    variant="rounded"
                    src={APP_LOGOS.icivics}
                    sx={{ width: 40, height: 40, bgcolor: '#fff7f2', p: 0.5 }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                      {CLASS.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#6b6f76' }}>
                      {TEACHER.short} · {CLASS.app}
                    </Typography>
                  </Box>
                </Stack>
              </Card>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: 28, textTransform: 'none', fontWeight: 700,
                  py: 1.1, bgcolor: '#5e35b1', mt: 1,
                }}
              >
                Open iCivics
              </Button>
              <Button
                variant="text"
                fullWidth
                onClick={() => setState('entry')}
                sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6b6f76' }}
              >
                Join another class
              </Button>
            </Stack>
          )}
        </Box>

        <BottomNavigation
          showLabels
          value={2}
          sx={{
            borderTop: '1px solid #ececf2',
            bgcolor: '#fff',
            height: 56,
            '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
            '& .MuiBottomNavigationAction-label': { fontSize: '0.62rem', fontWeight: 600 },
          }}
        >
          <BottomNavigationAction label="Dashboard" icon={<HomeIcon />} />
          <BottomNavigationAction label="Progress" icon={<ProgressIcon />} />
          <BottomNavigationAction label="Join Class" icon={<JoinIcon />} />
          <BottomNavigationAction label="Profile" icon={<PersonIcon />} />
        </BottomNavigation>

        <NavBar />
      </PhoneFrame>
    </Box>
  );
};

export default AndroidJoinClassMockup;
